import React, { useState } from 'react';
import { useIntl } from "react-intl";
import { DictType, getLabelKeyFromValue, LabelKeyValueItem, LocaleIntlKeys, LocaleTypes, SystemEntity } from '@/model';
import { useAgent, useDeactivateAgent, useUpdateAgent } from "@/query/agent";
import { useAgentId, useDialog, useRouteHandler } from "@/hook";
import Button from "@/component/Button";
import { errorToast, successToast } from "@/component/Toast";
import { DropdownButton, DropdownGroup, DropdownItem, DropdownItems, RangeSlider, TextInputGroup } from "@/component/input";

import AgentTokenFrom from "./component/AgentTokenFrom";
import SystemEntityToggles from "./component/SystemEntityToggles";


interface AgentModifyForm {
    agent_id: number;
    agent_name: string;
    deep_finder_threshold: number;
    deep_finder_result_count: number;
    language: "ko" | "en";
    system_entity: DictType<SystemEntity>;
    embedding: "use_v3" | "ada";
}

const EMBEDDING_OPTIONS: LabelKeyValueItem[] = [
    { value: "use_v3", labelKey: "i01005_o1" },
    { value: "ada", labelKey: "i01005_o2" },
];

const SettingView: React.FC = () => {
    const intl = useIntl();
    const agentId = useAgentId();
    const dialog = useDialog();
    const { moveToAgentList } = useRouteHandler();

    const { data: agent } = useAgent(agentId);
    const { mutateAsync: updateAgent, isPending: isUpdating } = useUpdateAgent(agentId);
    const { mutateAsync: deactivateAgent } = useDeactivateAgent(agentId);

    const [validation, setValidation] = useState<{ agent_name: boolean }>({ agent_name: true });
    const [modifiedAgent, setModifiedAgent] = useState<AgentModifyForm>({
        agent_id: agentId,
        agent_name: agent?.name ?? "",
        language: agent?.config.language ?? "ko",
        embedding: agent?.config.embedding ?? "use_v3",
        deep_finder_result_count: agent?.config.deep_finder_result_count ?? 3,
        deep_finder_threshold: agent?.config.deep_finder_threshold ?? 50,
        system_entity: agent?.config.system_entity ?? {},
    });

    const validate = () => {
        const currentValidation = {
            agent_name: !!modifiedAgent.agent_name && modifiedAgent.agent_name.trim().length > 0,
        };
        setValidation(currentValidation);
        return Object.values(currentValidation).every(value => value);
    };

    const onClickUpdate = async () => {
        if (!validate()) {
            return;
        }
        await updateAgent(modifiedAgent)
            .then(() => {
                successToast(intl.formatMessage({ id: "t003" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t004" }));
            });
    };

    const onClickDelete = async (e: React.MouseEvent) => {
        e.preventDefault();

        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i13017" }),
            content: intl.formatMessage({ id: "i13029" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: async () => {
                await deactivateAgent()
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t005" }));
                        moveToAgentList({ state: { needRefetch: true } });
                    })
                    .catch(() => {
                        errorToast(intl.formatMessage({ id: "t006" }));
                    });
            },
        });
    };

    return (
        <div className="agent-content-layout">
            <div className="w-sm-page flex flex-col gap-4">
                <div className="w-full pt-5.5 px-6 pb-8 flex flex-col gap-5 bg-white rounded-[6px] border border-black-200">
                    <TextInputGroup value={modifiedAgent.agent_name}
                                    invalid={!validation.agent_name}
                                    label={intl.formatMessage({ id: "i13002" })}
                                    placeholder={intl.formatMessage({ id: "i01003" })}
                                    onChange={v => setModifiedAgent(prev => ({ ...prev, agent_name: v }))}
                    />

                    <div className="flex flex-col gap-1.5">
                        <div className="input-group-heading">
                            {intl.formatMessage({ id: "i01004" })}
                        </div>
                        <DropdownGroup>
                            {({ open, close }) => (<>
                                <DropdownButton open={open} close={close}>
                                    {intl.formatMessage({ id: LocaleIntlKeys[modifiedAgent.language] })}
                                </DropdownButton>
                                <DropdownItems>
                                    {LocaleTypes.map((locale, index) => (
                                        <DropdownItem key={locale}
                                                      isOdd={index % 2 === 1}
                                                      onClick={() => setModifiedAgent(prev => ({ ...prev, language: locale }))}
                                        >
                                            {intl.formatMessage({ id: LocaleIntlKeys[locale] })}
                                        </DropdownItem>
                                    ))}
                                </DropdownItems>
                            </>)}
                        </DropdownGroup>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <div className="input-group-heading">
                            {intl.formatMessage({ id: "i01005" })}
                        </div>
                        <DropdownGroup>
                            {({ open, close }) => (<>
                                <DropdownButton open={open} close={close}>
                                    {intl.formatMessage({ id: getLabelKeyFromValue(EMBEDDING_OPTIONS, modifiedAgent.embedding!) })}
                                </DropdownButton>
                                <DropdownItems>
                                    {EMBEDDING_OPTIONS.map((option, index) => (
                                        <DropdownItem key={option.value}
                                                      isOdd={index % 2 === 1}
                                                      onClick={() => setModifiedAgent(prev => ({ ...prev, embedding: option.value }))}
                                        >
                                            {intl.formatMessage({ id: option.labelKey })}
                                        </DropdownItem>
                                    ))}
                                </DropdownItems>
                            </>)}
                        </DropdownGroup>
                    </div>

                    <AgentTokenFrom value={agent?.token} />

                    <div className="flex flex-col gap-3">
                        <div className="input-group-heading">
                            {`${intl.formatMessage({ id: "i13016" })} (${modifiedAgent.deep_finder_threshold})`}
                        </div>

                        <RangeSlider width="100%"
                                     defaultValue={modifiedAgent.deep_finder_threshold}
                                     onChange={v => setModifiedAgent(prev => ({ ...prev, deep_finder_threshold: v }))}
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <div className="input-group-heading">
                            {intl.formatMessage({ id: "i13027" })}
                        </div>

                        <DropdownGroup>
                            {({ open, close }) => (<>
                                <DropdownButton open={open} close={close}>
                                    {modifiedAgent.deep_finder_result_count}
                                </DropdownButton>
                                <DropdownItems>
                                    {[1, 2, 3].map((range, index) => (
                                        <DropdownItem key={range}
                                                        isOdd={index % 2 === 1}
                                                        onClick={() => setModifiedAgent(prev => ({ ...prev, deep_finder_result_count: range }))}
                                        >
                                            {range}
                                        </DropdownItem>
                                    ))}
                                </DropdownItems>
                            </>)}
                        </DropdownGroup>
                    </div>

                    <SystemEntityToggles defaultSystemEntity={modifiedAgent.system_entity}
                                         onChange={v => setModifiedAgent(prev => ({ ...prev, system_entity: v }))}
                    />
                </div>
                <div className="flex items-center justify-between">
                    <Button size="sm"
                            variant="danger"
                            disabled={isUpdating}
                            onClick={onClickDelete}
                    >
                        {intl.formatMessage({ id: "b0008" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={isUpdating || !validation}
                            onClick={onClickUpdate}
                    >
                        {intl.formatMessage({ id: "b0009" })}
                    </Button>
                </div>
            </div>
        </div>
   );
};

export default SettingView;